class HeaderController {
  constructor() {
    this.elements = this.initializeElements();
    this.config = {
      mobileBreakpoint: '(max-width: 768px)',
      stickyOffset: 0,
      cartPopupOffset: 68,
      menuDrawerOffset: 88,
    };

    this.init();
  }

  /**
   * Initialize and cache DOM elements
   * Returns an object with references to key elements
   */
  initializeElements() {
    return {
      header: document.querySelector('#shopify-section-header header'),
      openMobileNavigation: document.querySelector('#openMobileNavigation'),
      closeMobileNavigation: document.querySelector('#closeMobileNavigation'),
      navbarCollapse: document.querySelector('#navbarCollapse'),
      menuDrawerNavigationContainer: document.querySelector('.menu-drawer-navigation-container'),
      popupCartItemEl: document.querySelector('#mobile-popup-cart-item'),
      popupCartTargetEl: document.querySelector('[data-drawer-target="mobile-popup-cart-item"]'),
      popupCartHideBtnEl: document.querySelector('[data-drawer-hide="mobile-popup-cart-item"]'),
      cartItemBlockEl: document.querySelector('#mobile-popup-cart-item .responsive-cart-item-block'),
      profileDropdown: document.getElementById('profileDropdown'),
      logoutLinks: document.querySelectorAll('a[href^="/account/logout"]'),
      accountLinkBlocks: document.querySelectorAll('.header-profile-link-block .account-links'),
      submenuItems: document.querySelectorAll('.submenu-item'),
    };
  }

  /**
   * Initialize all header functionality
   * Binds events and sets up initial state
   */
  init() {
    this.setupScrollBehavior();
    this.setupMobileNavigation();
    this.setupCartPopup();
    this.setupAccountLinks();
    this.setupProfileDropdown();
    this.setupLogoutHandlers();
    this.setupMobileSubmenus();
    this.setupResponsiveHandlers();
    this.initialSetup();
  }

  /**
   * Setup scroll behavior and sticky header
   * Disables automatic scroll restoration and manages sticky header class
   */
  setupScrollBehavior() {
    // Disable automatic scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Throttled scroll handler for better performance
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => this.handleStickyHeader(), 10);
    });

    // Initial sticky header setup
    this.handleStickyHeader();
  }

  /**
   * Handle sticky header on scroll
   * Adds or removes sticky class based on scroll position
   */
  handleStickyHeader() {
    if (!this.elements.header) return;

    const stickyThreshold = this.elements.header.offsetTop;
    const shouldBeSticky = window.pageYOffset > stickyThreshold;

    this.elements.header.classList.toggle('sticky-header', shouldBeSticky);
  }

  /**
   * Setup mobile navigation handlers
   * Binds open/close events to mobile nav buttons
   */
  setupMobileNavigation() {
    // Open mobile navigation
    this.elements.openMobileNavigation?.addEventListener('click', () => {
      this.openMobileNav();
    });

    // Close mobile navigation
    this.elements.closeMobileNavigation?.addEventListener('click', () => {
      this.closeMobileNav();
    });
  }

  /**
   * Open mobile navigation
   * Applies styles and classes to show mobile navigation
   */
  openMobileNav() {
    const { navbarCollapse, menuDrawerNavigationContainer, closeMobileNavigation } = this.elements;

    if (!navbarCollapse || !menuDrawerNavigationContainer) return;

    // Show mobile navigation
    navbarCollapse.classList.add('block');
    navbarCollapse.classList.remove('hidden');
    navbarCollapse.style.height = this.calculateHeight();
    menuDrawerNavigationContainer.style.height = this.calculateHeight(this.config.menuDrawerOffset);
    closeMobileNavigation?.classList.add('navbarTogglerActive');

    // Lock body scroll
    this.toggleBodyScroll(true);
  }

  /**
   * Close mobile navigation
   * Resets styles and classes to hide mobile navigation
   */
  closeMobileNav() {
    const { navbarCollapse, menuDrawerNavigationContainer, closeMobileNavigation } = this.elements;

    if (!navbarCollapse || !menuDrawerNavigationContainer) return;

    // Hide mobile navigation
    navbarCollapse.classList.remove('block');
    navbarCollapse.classList.add('hidden');
    navbarCollapse.style.height = '';
    menuDrawerNavigationContainer.style.height = '';
    closeMobileNavigation?.classList.remove('navbarTogglerActive');

    // Unlock body scroll
    this.toggleBodyScroll(false);
  }

  /**
   * Setup cart popup handlers
   * Binds open/close events to cart popup buttons
   */
  setupCartPopup() {
    // Open cart popup
    this.elements.popupCartTargetEl?.addEventListener('click', () => {
      this.openCartPopup();
    });

    // Close cart popup
    this.elements.popupCartHideBtnEl?.addEventListener('click', () => {
      this.closeCartPopup();
    });
  }

  /**
   * Open cart popup
   * Updates height and locks body scroll
   */
  openCartPopup() {
    this.updateCartPopupHeight();
    this.toggleBodyScroll(true);
  }

  /**
   * Close cart popup
   * Resets styles and unlocks body scroll
   */
  closeCartPopup() {
    this.resetCartPopupHeight();
    this.toggleBodyScroll(false);
  }

  /**
   * Setup account link behaviors
   * Handles navigation and active states for account links
   */
  setupAccountLinks() {
    this.elements.accountLinkBlocks?.forEach((link) => {
      link.addEventListener('click', (event) => this.handleAccountLinkClick(event, link));

      // Set active link based on current hash
      const href = link.getAttribute('href');

      if (href === window.location.hash) {
        link.classList.add('active-link');
      }
    });
  }

  /**
   * Handle account link click
   * Navigates to account section and updates active states
   */
  handleAccountLinkClick(event, clickedLink) {
    event.preventDefault();

    const href = clickedLink.getAttribute('href');
    if (!href) return;

    const redirectUrl = `${location.origin}${PAGE_URLS.ACCOUNT}${href}`;

    // If already on account page, just change hash and reload
    if (location.pathname === PAGE_URLS.ACCOUNT) {
      location.hash = href;
      location.reload();
    } else {
      // Navigate to account page with hash
      location.href = redirectUrl;
    }

    // Update active states
    this.elements.accountLinkBlocks.forEach((item) => item.classList.remove('active-link'));
    clickedLink.classList.add('active-link');
  }

  /**
   * Setup profile dropdown handlers
   * Binds click events to dropdown items to close the dropdown
   */
  setupProfileDropdown() {
    this.elements.profileDropdown?.querySelectorAll('.profile-dropdown-item').forEach((item) => {
      item.addEventListener('click', () => {
        this.elements.profileDropdown.classList.add('hidden');
      });
    });
  }

  /**
   * Setup logout handlers
   * Binds click events to logout links
   */
  setupLogoutHandlers() {
    this.elements.logoutLinks.forEach((link) => {
      link.addEventListener('click', (event) => this.handleLogout(event));
    });
  }

  /**
   * Handle logout process
   * Performs logout API call and redirects to login page
   */
  async handleLogout(event) {
    event.preventDefault();
    const exceptionMessage = window.errorMessage;
    let isRedirecting = false;

    try {
      toggleFullScreenPreloader(true);

      // Call logout API
      await fetchData(SHOPIFY_API.CUSTOMER_LOGOUT);

      // Clear session data
      clearAuthFlowState();
      deleteSessionStorageItem(SESSION_KEY.PROFILE);
      deleteSessionStorageItem(SESSION_KEY.CHECKOUT);
      deleteSessionStorageItem(SESSION_KEY.RETURN_TO);

      // Set redirecting flag and redirect to login page (keep loader active during redirect)
      isRedirecting = true;
      window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
    } catch (err) {
      showToastMessage(exceptionMessage.somethingWentWrong);
      console.error('Logout Error:', err);
    } finally {
      // Only remove loading state if we're not redirecting
      if (!isRedirecting) {
        toggleFullScreenPreloader(false);
      }
    }
  }

  /**
   * Setup mobile submenu toggles
   * Binds click events to submenu items for toggling
   */
  setupMobileSubmenus() {
    if (!this.isMobile()) return;

    // Bind click events to submenu items
    this.elements.submenuItems.forEach((el) => {
      const anchor = el.querySelector('a');
      anchor?.addEventListener('click', (event) => this.handleSubmenuToggle(event, el, anchor));
    });
  }

  /**
   * Handle submenu toggle
   * Toggles active states and visibility of submenu
   */
  handleSubmenuToggle(event, submenuItem, anchor) {
    event.preventDefault();

    submenuItem.classList.toggle('submenu-item-active');
    anchor.classList.toggle('active');
    anchor.querySelector('.caret-icon svg')?.classList.toggle('active');
    submenuItem.querySelector('.submenu')?.classList.toggle('hidden');
  }

  /**
   * Setup responsive event handlers
   * Handles window resize and orientation changes
   */
  setupResponsiveHandlers() {
    const handleResize = () => {
      this.updateResponsiveHeights();
      this.updateCartPopupHeight();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
  }

  /**
   * Initial setup calls
   */
  initialSetup() {
    this.updateResponsiveHeights();
    this.updateCartPopupHeight();
  }

  /**
   * Calculate height with optional reduction
   * Returns height string for CSS usage
   */
  calculateHeight(reduceHeight = 0) {
    return `${window.innerHeight - reduceHeight}px`;
  }

  /**
   * Check if current viewport is mobile
   * Returns true if viewport matches mobile breakpoint
   */
  isMobile() {
    return window.matchMedia(this.config.mobileBreakpoint).matches;
  }

  /**
   * Update responsive heights for mobile elements
   * Applies calculated heights to navigation containers
   */
  updateResponsiveHeights() {
    const { navbarCollapse, menuDrawerNavigationContainer } = this.elements;
    if (!navbarCollapse || !menuDrawerNavigationContainer) return;

    if (this.isMobile()) {
      navbarCollapse.style.height = this.calculateHeight();
      menuDrawerNavigationContainer.style.height = this.calculateHeight(this.config.menuDrawerOffset);
    } else {
      [navbarCollapse, menuDrawerNavigationContainer].forEach((el) => {
        if (el) {
          el.style.height = '';
          el.style.overflowY = '';
        }
      });
    }
  }

  /**
   * Update cart popup height for mobile
   * Applies styles to ensure proper height and scrolling
   */
  updateCartPopupHeight() {
    const dynamicCartItemBlock = document.querySelector('#mobile-popup-cart-item .responsive-cart-item-block');

    if (dynamicCartItemBlock && this.isMobile()) {
      Object.assign(dynamicCartItemBlock.style, {
        height: `${window.innerHeight - this.config.cartPopupOffset}px`,
        overflowY: 'auto',
        position: 'fixed',
        width: '100%',
        paddingBottom: 'env(safe-area-inset-bottom)',
      });
    }
  }

  /**
   * Reset cart popup styles
   * Removes inline styles applied to cart item block
   */
  resetCartPopupHeight() {
    this.elements.cartItemBlockEl?.removeAttribute('style');
  }

  /**
   * Toggle body scroll lock
   * Applies or removes styles to lock/unlock body scroll
   */
  toggleBodyScroll(lock = true) {
    const styles = lock
      ? { overflowY: 'hidden', position: 'fixed', width: '100%' }
      : { overflowY: '', position: '', width: '' };

    Object.assign(document.body.style, styles);
  }
}

// Initialize header controller when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HeaderController();
});
